import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, X, FileText, Plus, MoreHorizontal, Eye, Edit2, Trash2, Paperclip, Link2, Calendar,
} from 'lucide-react';
import { expenseAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import Select from '../Select';
import SortHeader, { useSort, sortRows } from '../SortHeader';
import TablePager, { usePagedRows } from '../TablePager';
import { onActivateKey, ROW_FOCUS_RING } from '../../utils/keyboard';
import EntryDetailModal from './EntryDetailModal';
import {
  DatePresetPills, getDatePreset, formatCurrency, formatDate, formatSignedAmount, referenceOf,
  TypeBadge, PaymentBadge, ENTRY_TYPE_CONFIG, PAYMENT_MODE_CONFIG, LoadingRow, errorText,
} from './shared';

// Every line of the ledger for the chosen department, filtered by date, kind, method, category
// and a search over the description, notes and references. A row opens its full record; the
// three dots edit or delete it.

const ActionMenu = ({ id, onView, onEdit, onDelete, openMenuId, setOpenMenuId, canEdit }) => {
  const menuRef = useRef(null);
  const isOpen = openMenuId === id;

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      menuRef.current?.querySelector('button')?.focus();
      setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [isOpen, setOpenMenuId]);

  return (
    <div className="relative" ref={menuRef} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpenMenuId(isOpen ? null : id)}
        title="More actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <MoreHorizontal className="h-5 w-5 text-gray-500" />
      </button>
      {isOpen && (
        <div role="menu" className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-gray-200 py-1.5 z-30">
          <button role="menuitem" onClick={() => { onView(); setOpenMenuId(null); }} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
            <Eye className="h-4 w-4 text-gray-400" /> View
          </button>
          {canEdit && (
            <>
              <button role="menuitem" onClick={() => { onEdit(); setOpenMenuId(null); }} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                <Edit2 className="h-4 w-4 text-gray-400" /> Edit
              </button>
              <button role="menuitem" onClick={() => { onDelete(); setOpenMenuId(null); }} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                <Trash2 className="h-4 w-4 text-red-400" /> Delete
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ExpenseEntries = ({ department, cats, refreshKey, can, onEdit, onAdd, refresh }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const canEdit = can('VIEW_ENTRIES_TAB');

  // Everything by default, as the reporting app listed it: bills are entered in batches, often
  // weeks after the month, so "this month" would greet the bookkeeper with an empty page.
  const [preset, setPreset] = useState('all');
  const [range, setRange] = useState(() => getDatePreset('all'));
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [modeFilter, setModeFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cashInHand, setCashInHand] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const { sortConfig, handleSort } = useSort('entryDate', 'desc');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // The category filter belongs to the department; a new department starts it over.
  useEffect(() => { setCategoryFilter('ALL'); }, [department]);

  const scopeId = categoryFilter !== 'ALL' ? categoryFilter : department !== 'ALL' ? department : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await expenseAPI.getEntries({
        from: range.from,
        to: range.to,
        type: typeFilter !== 'ALL' ? typeFilter : undefined,
        paymentMode: modeFilter !== 'ALL' ? modeFilter : undefined,
        categoryId: scopeId,
        search: debounced || undefined,
      });
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast('error', errorText(err, 'Failed to load entries'));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [range, typeFilter, modeFilter, scopeId, debounced, showToast]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // The running balance for the department: a position, not a period total, so never date-filtered.
  useEffect(() => {
    let alive = true;
    expenseAPI.getCashPosition(department !== 'ALL' ? department : undefined)
      .then(d => { if (alive) setCashInHand(d?.closing ?? null); })
      .catch(() => { if (alive) setCashInHand(null); });
    return () => { alive = false; };
  }, [department, refreshKey]);

  const categoryOptions = useMemo(() => {
    const list = department !== 'ALL'
      ? cats.childrenOf(department).map(c => ({ value: c.id, label: c.name }))
      : cats.flat.filter(c => c.active !== false).map(c => ({ value: c.id, label: c.parentName ? `${c.parentName} › ${c.name}` : c.name }));
    return [{ value: 'ALL', label: department !== 'ALL' ? 'All categories' : 'All departments and categories' }, ...list];
  }, [cats, department]);

  const sorted = useMemo(() => sortRows(entries, sortConfig, {
    entryDate: e => e.entryDate,
    description: e => (e.description || '').toLowerCase(),
    amount: e => Number(e.amount || 0),
    category: e => (e.categoryPath || '').toLowerCase(),
    paymentMode: e => e.paymentMode,
  }), [entries, sortConfig]);

  const paging = usePagedRows(sorted, {
    id: 'expense-entries',
    resetOn: [sortConfig, debounced, typeFilter, modeFilter, categoryFilter, range, department],
  });

  const totals = useMemo(() => {
    let spent = 0, withdrawn = 0;
    for (const e of entries) {
      if (e.entryType === 'WITHDRAWAL') withdrawn += Number(e.amount || 0);
      else spent += Number(e.amount || 0);
    }
    return { spent, withdrawn, count: entries.length };
  }, [entries]);

  const hasFilters = search || typeFilter !== 'ALL' || modeFilter !== 'ALL' || categoryFilter !== 'ALL';
  const clearFilters = () => { setSearch(''); setTypeFilter('ALL'); setModeFilter('ALL'); setCategoryFilter('ALL'); };

  const remove = async (entry) => {
    const linked = entry.entryType === 'EXPENSE' && entry.paymentMode !== 'CASH'
      ? ' The withdrawal recorded with it goes too.' : '';
    const ok = await confirm({
      title: 'Delete this entry?',
      subtitle: 'This cannot be undone',
      message: `"${entry.description}" for ${formatCurrency(entry.amount)} on ${formatDate(entry.entryDate)} will be removed from the ledger.${linked}`,
      confirmText: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await expenseAPI.deleteEntry(entry.id);
      showToast('success', 'Entry deleted');
      refresh();
    } catch (err) {
      showToast('error', errorText(err, 'Failed to delete the entry'));
    }
  };

  const departmentName = department !== 'ALL' ? (cats.byId[department]?.name || 'this department') : 'all departments';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4">
          <p className="text-gray-500 text-sm">Entries shown</p>
          <p className="text-2xl font-bold text-gray-900">{totals.count}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4">
          <p className="text-gray-500 text-sm">Spent</p>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(totals.spent)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4">
          <p className="text-gray-500 text-sm">Withdrawn</p>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(totals.withdrawn)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4">
          <p className="text-gray-500 text-sm">Cash in hand, {departmentName}</p>
          <p className={`text-2xl font-bold ${Number(cashInHand) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
            {cashInHand === null ? '—' : formatCurrency(cashInHand)}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5">All time, whatever the dates above</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-5 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Calendar className="h-5 w-5 text-gray-400" />
          <DatePresetPills value={preset} onChange={(p) => { setPreset(p); setRange(getDatePreset(p)); }} />
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search description, notes, cheque or reference"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            />
          </div>
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            options={[{ value: 'ALL', label: 'Expenses and withdrawals' }, ...Object.entries(ENTRY_TYPE_CONFIG).map(([k, v]) => ({ value: k, label: `${v.label}s` }))]}
            className="min-w-[200px]"
            buttonClassName="px-4 py-3 rounded-xl"
          />
          <Select
            value={modeFilter}
            onChange={setModeFilter}
            options={[{ value: 'ALL', label: 'Any method' }, ...Object.entries(PAYMENT_MODE_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))]}
            className="min-w-[160px]"
            buttonClassName="px-4 py-3 rounded-xl"
          />
          <Select
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={categoryOptions}
            className="min-w-[200px]"
            buttonClassName="px-4 py-3 rounded-xl"
          />
          {hasFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1.5 px-4 py-3 text-gray-500 hover:text-gray-700 transition-colors">
              <X className="h-4 w-4" /> Clear
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xl">
        {loading && entries.length === 0 ? (
          <LoadingRow text="Loading entries..." />
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <FileText className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No entries</p>
            <p className="text-gray-400 text-sm mt-1">{hasFilters ? 'Try other filters or dates' : 'Nothing entered for these dates yet'}</p>
            {!hasFilters && (
              <button onClick={onAdd} className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium">
                <Plus className="h-4 w-4 inline mr-1" /> Add expense
              </button>
            )}
          </div>
        ) : (
          <>
            <TablePager paging={paging} noun="entry" nounPlural="entries" className="px-4" />
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <SortHeader label="Date" sortKey="entryDate" sortConfig={sortConfig} onSort={handleSort} />
                  <SortHeader label="Description" sortKey="description" sortConfig={sortConfig} onSort={handleSort} />
                  <SortHeader label="Department" sortKey="category" sortConfig={sortConfig} onSort={handleSort} />
                  <SortHeader label="Paid by" sortKey="paymentMode" sortConfig={sortConfig} onSort={handleSort} />
                  <SortHeader label="Amount" sortKey="amount" sortConfig={sortConfig} onSort={handleSort} align="right" />
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paging.pageRows.map((entry) => {
                  const open = () => setDetailId(entry.id);
                  const isWithdrawal = entry.entryType === 'WITHDRAWAL';
                  return (
                    <tr
                      key={entry.id}
                      tabIndex={0}
                      onClick={open}
                      onKeyDown={onActivateKey(open)}
                      className={`border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer ${ROW_FOCUS_RING}`}
                    >
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(entry.entryDate)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900 font-medium truncate max-w-xs" title={entry.description}>{entry.description}</span>
                          {Number(entry.attachmentCount) > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-xs text-gray-400" title={`${entry.attachmentCount} file(s)`}>
                              <Paperclip className="h-3.5 w-3.5" />{entry.attachmentCount}
                            </span>
                          )}
                          {entry.linkedEntryId && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5" title="Recorded with the expense it pays">
                              <Link2 className="h-3 w-3" /> with expense
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5"><TypeBadge type={entry.entryType} /></div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{entry.categoryPath || <span className="text-gray-300">-</span>}</td>
                      <td className="px-4 py-3">
                        <PaymentBadge mode={entry.paymentMode} />
                        {referenceOf(entry) && (
                          <div className="text-xs text-gray-500 font-mono mt-1">
                            {referenceOf(entry)}{entry.chequeBank ? ` · ${entry.chequeBank}` : ''}
                          </div>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm font-semibold text-right whitespace-nowrap ${isWithdrawal ? 'text-blue-700' : 'text-red-700'}`}>
                        {formatSignedAmount(entry)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ActionMenu
                          id={entry.id}
                          canEdit={canEdit}
                          onView={open}
                          onEdit={() => onEdit(entry)}
                          onDelete={() => remove(entry)}
                          openMenuId={openMenuId}
                          setOpenMenuId={setOpenMenuId}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TablePager paging={paging} noun="entry" nounPlural="entries" position="bottom" className="px-4" />
          </>
        )}
      </div>

      {detailId && (
        <EntryDetailModal
          entryId={detailId}
          canEdit={canEdit}
          onClose={() => setDetailId(null)}
          onEdit={(detail) => { setDetailId(null); onEdit(detail); }}
        />
      )}
    </div>
  );
};

export default ExpenseEntries;
